/**
 * @file UserInfo.jsx
 * @description Componente que muestra el nombre del usuario autenticado en la barra de navegación superior.
 * 
 * Este componente consulta los datos del usuario desde la API utilizando su `username`,
 * y renderiza su nombre y apellido en un bloque de bienvenida visible en pantallas grandes.
 */

import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { Nav, Row } from 'react-bootstrap';

import { fetchUsers } from 'api/users/users';
import { handleApiError } from 'utils';

import styles from 'assets/scss/navbar/top/userInfo.module.scss';

/**
 * Componente funcional que obtiene y muestra los datos del usuario autenticado.
 * 
 * Utiliza el `username` y `token` almacenados en el estado global para hacer una llamada
 * a la API y obtener el nombre y apellido del usuario. El contenido es visible solo en pantallas medianas en adelante.
 * 
 * @returns {JSX.Element} Bloque de navegación con mensaje de bienvenida y nombre del usuario.
 */
const UserInfo = () => { 
	const token = useSelector(state => state.authentication.user?.token);
	const username = useSelector(state => state.authentication.user?.username);

	const [user, setUser] = useState({});

	useEffect(() => {
		if (token !== null && token !== undefined) {
			handleUserByUsername();
		}
	}, [token]);

	/**
	 * Consulta los datos del usuario por `username` y actualiza el estado local.
	 */
	const handleUserByUsername = async () => {
		try {
			const { data } = await fetchUsers({ username: encodeURIComponent(username) }, token);
			setUser(data);
		} catch (error) {
			handleApiError(error);
		}
	};

	return (
		<Nav.Item as="li" className="d-none d-sm-block">
			<Nav.Link>
				<Row className={styles.welcome}>
					<span className={`${styles.centerText} text-600 fw-normal`}>
						Bienvenido
					</span>
				</Row>
				<Row>
					<span className={`${styles.centerText} text-600 fw-normal`}>
						{user.name} {user.lastname}
					</span>
				</Row>
			</Nav.Link>
		</Nav.Item>
	);
};

export default UserInfo;